import { skipToken } from '@reduxjs/toolkit/query';
import BaseMap from 'components/BaseMap/BaseMap';
import ConfirmModal from 'components/UI/ConfirmPopup';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import BaseSearchDropdown from 'features/components/BaseSearchDropdown';
import { USGS_PROVISIONAL_STATEMENT } from 'features/Integrations/features/ConnectedDevices/assets/USGSProvisionalStatment';
import { getBuoyDevices } from 'features/Integrations/features/ConnectedDevices/features/Buoy/graph/buoyGraphUtils';
import MapLegendIcon from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/assets/MapLegendIcon.svg';
import DeviceMapLegend from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/features/components/DeviceMapLegend';
import USGSMapLayer from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/features/USGSMapLayer';
import { getMapCoords } from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/utils/DeviceMapUtils';
import useLocation from 'hooks/useLocation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetEveryUSGSLocationQuery } from 'services/geography/usgs/usgs.api';
import { useFusionGetIntegrationsQuery } from 'services/integrations/integrations.api';
import {
  selectUsgsProvisonalAccepted,
  setUsgsProvisonalAccepted,
} from '../../../../../../store/slices/Widget/widgetSlice';

const USGS_SOFT_MAX_BOUNDS = [
  [7, -171],
  [74, -50],
];

/**
 * Shared USGS Map feature component.
 *
 * Used by both the full-screen USGS page and the USGS map widget — no duplicate logic.
 *
 * @param {boolean} widget - When true, renders compact widget-style header and uses tighter zoom bounds.
 */
const USGSMap = ({ widget = false }) => {
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [showProvisionalModal, setShowProvisionalModal] = useState(false);
  const dispatch = useDispatch();
  const provisionalAccepted = useSelector(selectUsgsProvisonalAccepted);
  const { locations } = useLocation();
  const coords = locations.workplaceLocation.coords;
  const initialBounds = locations.workplaceLocation.bounds;
  const [mapCenter, setMapCenter] = useState(getMapCoords(coords[0], coords[1]));
  const [mapZoom, setMapZoom] = useState(widget ? 16 : 5);
  const [mapBounds, setMapBounds] = useState(initialBounds);
  const [legendToggle, setLegendToggle] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchError, setSearchError] = useState('');
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [disableUSGSClustering, setDisableUSGSClustering] = useState(true);

  const {
    data: usgsPoints = [],
    isLoading: usgsLoading,
    isFetching: usgsFetching,
  } = useGetEveryUSGSLocationQuery(provisionalAccepted ? true : skipToken);

  const isUSGSConnected = useMemo(
    () => integrations?.some((integration) => integration?.system_type === 'USGS'),
    [integrations],
  );
  const hasBuoyDevices = useMemo(
    () => getBuoyDevices(connectedDevices).length > 0,
    [connectedDevices],
  );

  const sortedUSGSPoints = useMemo(
    () =>
      [...(Array.isArray(usgsPoints) ? usgsPoints : [])].sort((a, b) =>
        String(a.name || '')
          .toLowerCase()
          .localeCompare(String(b.name || '').toLowerCase()),
      ),
    [usgsPoints],
  );

  useEffect(() => {
    // Update center when the chosen coords change.
    setMapCenter(getMapCoords(coords[0], coords[1]));
  }, [coords]);

  useEffect(() => {
    // Update bounds when the preferred bounds (jurisdiction or effective) change.
    setMapBounds(initialBounds ?? null);
  }, [initialBounds]);

  const handleUSGSSearch = (USGSName) => {
    const trimmedName = String(USGSName || '').trim();
    if (!trimmedName) {
      setSearchError('Please enter a site name to search for.');
      return;
    }
    const foundPoint = Array.isArray(usgsPoints)
      ? usgsPoints.find(
          (point) =>
            String(point.name || '')
              .trim()
              .toLowerCase() === trimmedName.toLowerCase(),
        )
      : null;
    if (!foundPoint || !foundPoint.coordinates) {
      setSearchError('No matching site found. Use exact site name.');
      return;
    }
    setSearchError('');
    setSelectedSiteName(String(foundPoint.name || ''));
    setMapCenter([Number(foundPoint.coordinates.lat), Number(foundPoint.coordinates.lng)]);
    setMapZoom(14);
  };

  const handleUSGSSelect = (siteName) => {
    setSelectedSiteName(siteName);
    if (!siteName) {
      setSearchError('');
      return;
    }
    handleUSGSSearch(siteName);
  };

  const widgetSearchBar = provisionalAccepted && (
    <BaseSearchDropdown
      className="mb-1 pt-1"
      searchValue={searchValue}
      onSearchValueChange={(value) => {
        setSearchValue(value);
        setSearchError('');
      }}
      onSearch={handleUSGSSearch}
      searchPlaceholder="Search USGS site name"
      searchAriaLabel="Search USGS site by exact name"
      selectedValue={selectedSiteName}
      onSelect={handleUSGSSelect}
      selectAriaLabel="Select USGS site"
      defaultOptionLabel="All USGS sites"
      options={sortedUSGSPoints.map((point) => ({
        value: point.name || '',
        label: point.name || point.site_no || 'USGS site',
      }))}
    />
  );

  const searchBar = provisionalAccepted && (
    <div className="d-flex flex-column gap-1 flex-grow-1" style={{ maxWidth: 480 }}>
      <div className="d-flex gap-2">
        <Form.Control
          type="search"
          placeholder="Enter exact USGS site name"
          aria-label="Search USGS site by exact name"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setSearchError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleUSGSSearch(searchValue);
            }
          }}
        />
        <Button variant="primary" onClick={() => handleUSGSSearch(searchValue)}>
          Search
        </Button>
      </div>
      {searchError && <div className="text-danger small mt-1">{searchError}</div>}
    </div>
  );

  return (
    <div
      className={`d-flex flex-column h-100 w-100 ${widget ? 'overflow-hidden rounded-3' : 'overflow-hidden'}`}
    >
      {/* Header */}
      {widget ? (
        <>
          <div className="d-flex justify-content-between flex-row align-items-center py-1 flex-shrink-0">
            <p className="mb-0 small" style={{ fontSize: 8 }}>
              USGS Data may be{' '}
              <span
                className="text-decoration-underline text-white cursor-pointer"
                onClick={() => setShowProvisionalModal(true)}
              >
                provisional
              </span>
            </p>
            <p className="text-end mb-0 small" style={{ fontSize: 8 }}>
              Source: U.S. Geological Survey - National Water Dashboard
            </p>
          </div>
          {widgetSearchBar}
          {searchError && <div className="text-danger small mb-1">{searchError}</div>}
        </>
      ) : (
        <div className="d-flex align-items-center justify-content-between gap-3 px-1 pb-2 flex-shrink-0 flex-wrap">
          <div className="d-flex flex-column">
            <h5 className="mb-0 fw-bold">USGS Monitoring Stations Map</h5>
            <p className="gap-3 d-flex">
              <span className="text-white-50" style={{ fontSize: 11 }}>
                USGS data may be{' '}
                <span
                  className="text-decoration-underline cursor-pointer text-white"
                  onClick={() => setShowProvisionalModal(true)}
                >
                  provisional
                </span>
                . Accept the disclaimer to view the map.
              </span>
              <span className="text-white-50" style={{ fontSize: 11 }}>
                Source: U.S. Geological Survey — National Water Dashboard
              </span>
            </p>
          </div>
          {searchBar}
        </div>
      )}

      {!widget && <WidgetTimeUpdate route="getEveryUSGSLocation" addMargin />}

      {/* Map area */}
      <div
        className={`flex-grow-1 min-h-0 ${widget ? 'rounded-3 overflow-hidden' : ''}`}
        style={{ position: 'relative', isolation: 'isolate' }}
      >
        {/* Disclaimer gate overlay */}
        {!provisionalAccepted && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 4,
              background: widget ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.55)',
              color: '#fff',
            }}
          >
            <div className="text-center p-4">
              {!widget && <h5 className="mb-3">USGS Provisional Data Disclaimer</h5>}
              <p className={`mb-3 ${widget ? '' : 'text-white-50'}`}>
                {widget
                  ? 'USGS data may be provisional.'
                  : 'USGS data may be provisional and subject to revision.'}
                <br />
                {widget
                  ? 'Accept disclaimer to view the map.'
                  : 'Accept the disclaimer to view the map.'}
              </p>
              <Button variant="light" onClick={() => setShowProvisionalModal(true)}>
                {widget ? 'View Disclaimer' : 'View & Accept Disclaimer'}
              </Button>
            </div>
          </div>
        )}

        {/* Map */}
        {provisionalAccepted && (
          <BaseMap
            mapCenter={mapCenter}
            zoom={mapZoom}
            setMapZoom={setMapZoom}
            bounds={mapBounds}
            setMapBounds={setMapBounds}
            maxZoom={widget ? 16 : 19}
            minZoom={widget ? 3 : 2}
            maxBounds={USGS_SOFT_MAX_BOUNDS}
            maxBoundsViscosity={0.75}
            filtersCompact
            deviceTypeToFilter="USGS"
            extraStack={
              <Form.Check
                type="switch"
                id={widget ? 'usgs-disable-clustering' : 'usgs-fullscreen-disable-clustering'}
                label={<span className="text-dark cursor-pointer">Disable USGS Clustering</span>}
                className="text-dark cursor-pointer"
                checked={disableUSGSClustering}
                onChange={() => setDisableUSGSClustering((prev) => !prev)}
              />
            }
          >
            {isUSGSConnected && (
              <USGSMapLayer
                showAllUSGSDevices={true}
                usgsPoints={usgsPoints}
                setMapCenter={setMapCenter}
                clusteringEnabled={!disableUSGSClustering}
                setMapZoom={setMapZoom}
              />
            )}
          </BaseMap>
        )}

        {/* Loading indicator */}
        {(usgsLoading || usgsFetching) && (
          <div
            style={{
              position: 'absolute',
              left: 8,
              bottom: '20%',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <Button
              variant="secondary"
              disabled
              className="p-1 opacity-100"
              style={{ pointerEvents: 'none' }}
            >
              Loading USGS Devices...
            </Button>
          </div>
        )}

        {/* Legend & info buttons */}
        {provisionalAccepted && isUSGSConnected && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              bottom: 8,
              zIndex: 3,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}
          >
            <div
              className="d-inline-flex flex-row align-items-end justify-content-end gap-0"
              style={{
                height: '100%',
                maxHeight: '100%',
                maxWidth: '100%',
                overflow: 'visible',
                width: 'max-content',
                position: 'relative',
                pointerEvents: 'auto',
              }}
            >
              <div className="d-flex flex-column justify-content-end gap-1">
                <Button
                  variant="primary"
                  className={`connected-devices-icon-button connected-devices-map-info-control text-white p-0${legendToggle ? ' connected-devices-control-selected' : ''}`}
                  style={{ width: 40, height: 40 }}
                  onClick={() => setShowProvisionalModal(true)}
                  title="View USGS disclaimer"
                >
                  <IoMdInformationCircleOutline size={25} />
                </Button>
                <Button
                  variant="primary"
                  className="connected-devices-icon-button connected-devices-map-info-control text-white p-0"
                  style={{ width: 40, height: 40 }}
                  onClick={() => setLegendToggle((prev) => !prev)}
                  aria-pressed={legendToggle}
                  title="Toggle map legend"
                >
                  <img
                    className="connected-devices-map-legend-icon"
                    src={MapLegendIcon}
                    alt="Map Legend"
                    height={25}
                    width={25}
                  />
                </Button>
              </div>
              {legendToggle && (
                <DeviceMapLegend
                  compact={widget}
                  setLegendToggle={setLegendToggle}
                  defaultBuoyOpen={!widget}
                  defaultUSGSOpen={true}
                  showBuoySection={!widget && hasBuoyDevices}
                  showUSGSSection={true}
                  style={{
                    height: '100%',
                    maxHeight: '100%',
                    maxWidth: widget ? 'min(300px, calc(100vw - 96px))' : 'min(360px, 100%)',
                    overflow: 'hidden',
                    width: widget ? 'min(300px, calc(100vw - 96px))' : 'min(360px, 100%)',
                    borderBottomRightRadius: 0,
                    borderTopRightRadius: 0,
                    marginLeft: 8,
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Provisional disclaimer modal */}
      <ConfirmModal
        open={showProvisionalModal}
        setOpen={setShowProvisionalModal}
        options={['Continue']}
        callbacks={[
          () => {
            setShowProvisionalModal(false);
            dispatch(setUsgsProvisonalAccepted(true));
          },
        ]}
        message={USGS_PROVISIONAL_STATEMENT}
        closeAction={() => {}}
        size="lg"
      />
    </div>
  );
};

export default USGSMap;
